import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, type OrderDetail, type PaymentMethod } from "@bizy/core";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { formatMoney } from "@/lib/format";
import { useSettlePayment } from "./api";
import { useState } from "react";
import { toast } from "sonner";

export interface SettlePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  order: OrderDetail;
}

/**
 * Future payment settlement (PRD §3.2.3): the remaining balance is pre-filled
 * from `total_amount - paid_amount`, and the Worker rejects anything above it.
 */
export const SettlePaymentDialog = ({ open, onClose, workspaceId, order }: SettlePaymentDialogProps) => {
  const [amount, setAmount] = useState<string>(String(order.balance_amount));
  const [method, setMethod] = useState<PaymentMethod>(PAYMENT_METHODS.cash);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const settle = useSettlePayment(workspaceId, order.id);

  const onSubmit = async () => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Enter an amount greater than zero.");
      return;
    }
    try {
      await settle.mutateAsync({
        amount: parsed,
        method,
        reference: reference.length > 0 ? reference : null,
        notes: notes.length > 0 ? notes : null,
      });
      toast.success("Payment settled and balance invoice issued.");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not settle the payment.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Settle payment"
      description={`Outstanding balance ${formatMoney(order.balance_amount, order.currency)}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={settle.isPending} onClick={() => void onSubmit()}>
            Record payment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Amount" htmlFor="settle-amount" required hint={`Max ${formatMoney(order.balance_amount, order.currency)}`}>
          <Input
            id="settle-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>

        <Field label="Method" htmlFor="settle-method">
          <Select
            id="settle-method"
            value={method}
            onChange={(event) => setMethod(event.target.value as PaymentMethod)}
          >
            {Object.values(PAYMENT_METHODS).map((value) => (
              <option key={value} value={value}>
                {PAYMENT_METHOD_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Reference" htmlFor="settle-reference" hint="Transfer id, QRIS reference, …">
          <Input
            id="settle-reference"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </Field>

        <Field label="Notes" htmlFor="settle-notes">
          <Textarea id="settle-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
};
